import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment";
import { renderCurrencyIcon } from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";

const COLOR_BORDER = "#E5E7EB";
const COLOR_TEXT = "#0D0D0E";
const COLOR_SUBTEXT = "#646F86";
const COLOR_MUTED = "#949DAA";

const Amount = ({ value, isHeading = false }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      fontWeight: 500,
      color: COLOR_TEXT,
      whiteSpace: "nowrap",
    }}
  >
    {renderCurrencyIcon(isHeading ? "black-small" : "black-xs-small")}
    {formattedAmountWithoutAed(Number(value) || 0)}
  </span>
);

const isInitialDeposit = (m) => {
  if (!m) return false;
  if (m.isInitialDeposit) return true;
  if ((m.type || "").toUpperCase() === "PROJECT_INITIAL_DEPOSIT") return true;
  return (
    (m.description || m.name || m.groupName || m.group_name || "")
      .trim()
      .toLowerCase() === "initial deposit"
  );
};

const milestoneLabelOf = (m) =>
  m?.description || m?.name || m?.groupName || m?.group_name || "-";

const milestoneDueDateOf = (m) => m?.due_date || m?.dueDate || null;

const parseDeliverables = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const PrimaryButton = ({ children, onClick, sx }) => (
  <Button
    variant="contained"
    size="small"
    onClick={onClick}
    sx={{
      backgroundColor: "#4D6BF6",
      color: "#fff",
      textTransform: "none",
      fontWeight: 500,
      fontSize: "12px",
      borderRadius: "6px",
      boxShadow: "none",
      padding: "0.5vh 0.7vw",
      whiteSpace: "nowrap",
      "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
      ...(sx || {}),
    }}
  >
    {children}
  </Button>
);

const SecondaryButton = ({ children, onClick, sx }) => (
  <Button
    variant="outlined"
    size="small"
    onClick={onClick}
    sx={{
      border: "1px solid #D7D8D9",
      backgroundColor: "#fff",
      color: "#0D0D0E",
      textTransform: "none",
      fontWeight: 500,
      fontSize: "12px",
      borderRadius: "6px",
      padding: "0.5vh 0.7vw",
      whiteSpace: "nowrap",
      "&:hover": { border: "1px solid #4D6BF6", color: "#4D6BF6", backgroundColor: "#fff", boxShadow: "1px 1px 2px rgba(0, 0, 0, 0.1)" },
      ...(sx || {}),
    }}
  >
    {children}
  </Button>
);

const MilestoneRow = ({
  milestone,
  onSendForApproval,
  onViewApproval,
  onRequestPayment,
  onViewPayment,
  // Send for Approval + Request Payment CTAs only make sense once the
  // proposal has been promoted to a project. While stage === "PROPOSAL"
  // we still render the milestone list (read-only) but suppress those
  // workflow buttons.
  ctasEnabled = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const deliverables = parseDeliverables(milestone.deliverables);
  const hasDeliverables = deliverables.length > 0;
  const isDeposit = isInitialDeposit(milestone);
  const label = milestoneLabelOf(milestone);
  const dueDate = milestoneDueDateOf(milestone);

  // Approval state — drives Send for Approval vs View Approval.
  // Empty delivery_approvals[] (or all DRAFT) → Send for Approval (when
  // there are deliverables to attach). Anything else → View Approval.
  const approvals = Array.isArray(milestone.delivery_approvals)
    ? milestone.delivery_approvals
    : [];
  const latestApproval = approvals[0] || null;
  const latestApprovalStatus = String(latestApproval?.status || "")
    .toUpperCase();
  const showSendForApproval =
    !latestApproval || latestApprovalStatus === "DRAFT";
  const showViewApproval =
    latestApproval && latestApprovalStatus !== "DRAFT";

  // Payment state — same shape as approvals, drives Request Payment vs
  // View Payment. Empty delivery_memos[] OR latest in DRAFT → Request
  // Payment (POST or PATCH on save). Otherwise → View Payment (read-only).
  const memos = Array.isArray(milestone.delivery_memos)
    ? milestone.delivery_memos
    : [];
  const latestMemo = memos[0] || null;
  const latestMemoStatus = String(latestMemo?.status || "").toUpperCase();
  const showRequestPayment =
    !latestMemo || latestMemoStatus === "DRAFT";
  const showViewPayment =
    latestMemo && latestMemoStatus !== "DRAFT";

  return (
    <Box
      sx={{
        mb: { xs: 0, md: 1.25 },
        borderRadius: { xs: 0, md: "8px" },
        overflow: "hidden",
        backgroundColor: expanded ? "#FAFAFB" : "transparent",
        border: { xs: "none", md: "1px solid transparent" },
        borderBottom: { xs: "1px solid #E5E7EB", md: "1px solid transparent" },
        boxShadow: expanded
          ? "0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.04)"
          : "none",
        transition:
          "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
      }}
    >
      {/* Mobile — row1: name + arrow, row2: due date + amount, row3: buttons 1:1. */}
      <Box
        onClick={hasDeliverables ? () => setExpanded((v) => !v) : undefined}
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 1,
          padding: "12px 14px",
          cursor: hasDeliverables ? "pointer" : "default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              color: COLOR_TEXT,
              fontWeight: 600,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
          {hasDeliverables ? (
            <IconButton
              size="small"
              sx={{ flexShrink: 0, width: 28, height: 28 }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, height: 28, flexShrink: 0 }} />
          )}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: COLOR_SUBTEXT, mb: 0.25 }}>
              Due Date
            </Typography>
            <Typography sx={{ fontSize: 13, color: COLOR_TEXT }}>
              {dueDate ? moment(dueDate).format("MMM DD, YYYY") : "-"}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: 11, color: COLOR_SUBTEXT, mb: 0.25 }}>
              Amount
            </Typography>
            <Box
              sx={{
                fontSize: 13,
                color: COLOR_TEXT,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Amount value={milestone.amount || milestone.milestone_amount || 0} />
            </Box>
          </Box>
        </Box>

        {(ctasEnabled && !isDeposit && (
          showSendForApproval || showViewApproval ||
          showRequestPayment || showViewPayment
        )) && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              "& > *": { width: "100%" },
            }}
          >
            {showSendForApproval && hasDeliverables && (
              <SecondaryButton
                onClick={() => onSendForApproval?.(milestone, latestApproval)}
              >
                Send for Approval
              </SecondaryButton>
            )}
            {showViewApproval && (
              <SecondaryButton
                onClick={() => onViewApproval?.(milestone, latestApproval)}
              >
                View Approval
              </SecondaryButton>
            )}
            {showRequestPayment && (
              <PrimaryButton
                onClick={() => onRequestPayment?.(milestone, latestMemo)}
              >
                Request Payment
              </PrimaryButton>
            )}
            {showViewPayment && (
              <PrimaryButton
                onClick={() => onViewPayment?.(milestone, latestMemo)}
              >
                View Delivery Memo
              </PrimaryButton>
            )}
          </Box>
        )}
      </Box>

      {/* Desktop — original single-row layout. */}
      <Box
        onClick={hasDeliverables ? () => setExpanded((v) => !v) : undefined}
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "row",
          alignItems: "center",
          gap: "16px",
          padding: "1vh 1vw",
          borderRadius: "8px",
          cursor: hasDeliverables ? "pointer" : "default",
          "&:hover": hasDeliverables ? { backgroundColor: "#F9FAFB" } : {},
        }}
      >
        {/* Milestone name */}
        <Typography
          sx={{
            flex: { md: 1 },
            minWidth: 0,
            fontSize: "14px",
            color: COLOR_TEXT,
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>

        {/* Due Date column */}
        <Box
          sx={{
            width: { xs: "100%", md: 140 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "flex-start", md: "flex-start" },
            fontSize: "13px",
            color: COLOR_SUBTEXT,
          }}
        >
          Due Date
          <span
            style={{ fontSize: "14px", color: COLOR_TEXT, marginTop: "4px" }}
          >
            {dueDate ? moment(dueDate).format("MMM DD, YYYY") : "-"}
          </span>
        </Box>

{/* Amount column */}
        <Box
          sx={{
            width: { xs: "100%", md: 120 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "flex-start", md: "flex-end" },
            textAlign: { md: "right" },
            fontSize: "13px",
            color: COLOR_SUBTEXT,
          }}
        >
          Amount
          <span
            style={{ fontSize: "14px", color: COLOR_TEXT, marginTop: "4px" }}
          >
            <Amount value={milestone.amount || milestone.milestone_amount || 0} />
          </span>
        </Box>

        {/* Actions column (fixed width so amount stays aligned) */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: { xs: "100%", md: 300 },
            flexShrink: 0,
            display: "flex",
            gap: 1,
            justifyContent: { xs: "flex-start", md: "flex-end" },
            flexWrap: "wrap",
          }}
        >
          {ctasEnabled && !isDeposit && showSendForApproval && hasDeliverables && (
            <SecondaryButton
              onClick={() => onSendForApproval?.(milestone, latestApproval)}
            >
              Send for Approval
            </SecondaryButton>
          )}
          {ctasEnabled && !isDeposit && showViewApproval && (
            <SecondaryButton
              onClick={() => onViewApproval?.(milestone, latestApproval)}
            >
              View Approval
            </SecondaryButton>
          )}
          {ctasEnabled && !isDeposit && showRequestPayment && (
            <PrimaryButton
              onClick={() => onRequestPayment?.(milestone, latestMemo)}
            >
              Request Payment
            </PrimaryButton>
          )}
          {ctasEnabled && !isDeposit && showViewPayment && (
            <PrimaryButton
              onClick={() => onViewPayment?.(milestone, latestMemo)}
            >
             View Delivery Memo
            </PrimaryButton>
          )}
        </Box>

        

        {/* Arrow */}
        <IconButton
          size="small"
          sx={{
            visibility: hasDeliverables ? "visible" : "hidden",
            flexShrink: 0,
            width: 32,
            height: 32,
            alignSelf: { xs: "flex-end", md: "center" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (hasDeliverables) setExpanded((v) => !v);
          }}
        >
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {hasDeliverables && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pl: { xs: 2, md: 4 }, pr: 2, pb: 2 }}>
            {deliverables.map((d, di) => {
              const subs = Array.isArray(d.sub_activities)
                ? d.sub_activities
                : [];
              return (
                <Box
                  key={d.id || di}
                  sx={{
                    py: 0.75,
                    borderBottom:
                      di === deliverables.length - 1
                        ? "none"
                        : "1px dashed #F3F4F6",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: COLOR_TEXT,
                      fontWeight: 500,
                    }}
                  >
                    {di + 1}.{" "}
                    {d.deliverable_description || d.name || "—"}
                  </Typography>
                  {subs.length > 0 && (
                    <Box sx={{ pl: 2, mt: 0.5 }}>
                      {subs.map((s, si) => (
                        <Typography
                          key={si}
                          sx={{
                            fontSize: 12,
                            color: "#6B7280",
                            py: 0.25,
                          }}
                        >
                          {di + 1}.{si + 1}{" "}
                          {s.sub_activity_description || s.name || "—"}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default function BillingMilestones({
  milestones: milestonesProp,
  loading,
  projectId,
  proposalId,
  stage,
  // Legacy callers may still pass `proposal` — fall back so we don't break
  // them. New callers pass `milestones` (from /api/projects/:id/billing-milestones).
  proposal,
}) {
  const ctasEnabled = String(stage || "").toUpperCase() === "PROJECT";
  const navigate = useNavigate();
  const routeProposalId = proposalId || projectId;

  const milestones = useMemo(() => {
    const fromProp = Array.isArray(milestonesProp) ? milestonesProp : null;
    const list =
      fromProp ||
      (Array.isArray(proposal?.billing_milestones)
        ? [...proposal.billing_milestones]
        : []);
    return [...list].sort(
      (a, b) =>
        (a.sortOrder ?? a.sort_order ?? 0) -
        (b.sortOrder ?? b.sort_order ?? 0)
    );
  }, [milestonesProp, proposal?.billing_milestones]);

  // Send for Approval — passes the approval id along when one already
  // exists in DRAFT (so the request-approval screen PATCHes instead of
  // POSTing). New approvals are created on first Save.
  const handleSendForApproval = (milestone, latestApproval) => {
    if (!projectId || !routeProposalId) return;
    const base = `/project/details/${routeProposalId}/billing-milestones/${milestone.id}/request-approval`;
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    if (proposalId) qs.set("proposal_id", String(proposalId));
    if (latestApproval?.id != null) {
      qs.set("approval_id", String(latestApproval.id));
    }
    navigate(`${base}?${qs.toString()}`);
  };

  const handleViewApproval = (milestone, latestApproval) => {
    if (!projectId || !routeProposalId || latestApproval?.id == null) return;
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
      approval_id: String(latestApproval.id),
      view: "1",
    });
    if (proposalId) qs.set("proposal_id", String(proposalId));
    navigate(
      `/project/details/${routeProposalId}/billing-milestones/${milestone.id}/request-approval?${qs.toString()}`
    );
  };

  // Request Payment — forwards memo id when one already exists in DRAFT
  // (so the screen PATCHes instead of POSTing). New memos are created on
  // first Save, exactly like the approval flow.
  const handleRequestPayment = (milestone, latestMemo) => {
    if (!projectId || !routeProposalId) return;
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    if (proposalId) qs.set("proposal_id", String(proposalId));
    if (latestMemo?.id != null) {
      qs.set("memo_id", String(latestMemo.id));
    }
    navigate(
      `/project/details/${routeProposalId}/billing-milestones/${milestone.id}/request-payment?${qs.toString()}`
    );
  };

  const handleViewPayment = (milestone, latestMemo) => {
    if (!projectId || !routeProposalId || latestMemo?.id == null) return;
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
      memo_id: String(latestMemo.id),
      view: "1",
    });
    if (proposalId) qs.set("proposal_id", String(proposalId));
    navigate(
      `/project/details/${routeProposalId}/billing-milestones/${milestone.id}/request-payment?${qs.toString()}`
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: COLOR_SUBTEXT }}>
        Loading milestones…
      </Box>
    );
  }

  if (!milestones.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: COLOR_SUBTEXT }}>
        No billing milestones added yet.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "none",
        p: 2,
        px: 0,
        mx: 0,
        m: 1,
        width: { xs: "100%", md: "80%" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {milestones.map((m) => (
          <MilestoneRow
            key={m.id}
            milestone={m}
            onSendForApproval={handleSendForApproval}
            onViewApproval={handleViewApproval}
            onRequestPayment={handleRequestPayment}
            onViewPayment={handleViewPayment}
            ctasEnabled={ctasEnabled}
          />
        ))}
      </Box>
    </Box>
  );
}
